/**
 * Company: SPHMedia
 * Description: [Follow trending topics block]
 */
import "./FollowTrendingTopics.css";

import FireIcon from "@assets/icons/fire.svg";
import ShuffleButton from "@elements/Buttons/ShuffleButton";
import FollowTopicsButton from "@elements/FollowTopicsButton/FollowTopicsButton";
import ViewFeedCTA, {
  ViewFeedCTAProps,
} from "@elements/ViewFeedCTA/ViewFeedCTA";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { ReactElement } from "react";

export type TopicsData = {
  label: string;
  topicId: string;
  isFollowing: boolean;
  isTrending?: boolean;
};

export type FollowTrendingTopicsProps = {
  topicsData: TopicsData[];
  onFollow: (topicIds: string, topicTitle: string) => Promise<void>;
  onUnfollow: (topicIds: string, topicTitle: string) => Promise<void>;
  onShuffle: () => Promise<void>;
  selected: string[];
  unSelect: (topicId: string, topicTitle: string) => Promise<void>;
  loadingTopicId: string | null;
  bhCustomEvent?: ReturnType<typeof useBHCustomEventAnalyticsCall>;
  userKeywords?: string;
} & ViewFeedCTAProps;

/**
 * FollowTrendingTopics
 * Description: [Follow trending topics block used in "For you" mybh page]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function FollowTrendingTopics({
  topicsData,
  onFollow,
  onUnfollow,
  onShuffle,
  selected,
  unSelect,
  loadingTopicId,
  isLoggedIn,
  followedIds,
  selectedIds,
  lightbox,
  variant = "widget",
  bhCustomEvent,
  userKeywords,
}: FollowTrendingTopicsProps): ReactElement {
  return (
    <div
      data-testid="follow-trending-topics"
      className="w-full trending-topics"
    >
      <div className="py-9 px-[15px]">
        <div className="flex flex-col gap-4 justify-center">
          <div className="flex justify-center items-center gap-1">
            <img
              src={FireIcon}
              width={32}
              height={32}
              alt="trending"
              data-testid="fire-header-icon"
            />
            <span className="text-text-inverse text-sm font-secondary font-semibold leading-[150%]">
              Ikuti topik terkini
            </span>
          </div>
          <div className="flex flex-col gap-3 items-center">
            <div className="flex flex-col items-start gap-2">
              <div className="flex gap-3 flex-wrap justify-center items-center">
                {topicsData.length > 0 ? (
                  topicsData.map((item, index) => (
                    <FollowTopicsButton
                      key={`${item.topicId}-${index}`}
                      variant="secondary"
                      label={item.label}
                      topicId={item.topicId}
                      onFollow={onFollow}
                      onUnfollow={onUnfollow}
                      isFollowing={item.isFollowing}
                      isTrending={item.isTrending}
                      selected={selected.includes(item.topicId) ? true : false}
                      unSelect={unSelect}
                      isLoading={loadingTopicId === item.topicId}
                    />
                  ))
                ) : (
                  <span className="text-text-inverse text-xxs">
                    No trending topics available
                  </span>
                )}
              </div>
              <div className="flex justify-center w-full">
                <ShuffleButton onShuffle={onShuffle} />
              </div>
            </div>
            <ViewFeedCTA
              isLoggedIn={isLoggedIn}
              followedIds={followedIds}
              selectedIds={selectedIds}
              lightbox={lightbox}
              variant={variant}
              bhCustomEvent={bhCustomEvent}
              userKeywords={userKeywords}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
